import axios, { AxiosError, type InternalAxiosRequestConfig } from 'axios'
import { API_URL } from '@/config/env'
import { useAuthStore } from '@/stores/authStore'
import { captureApiError } from '@/lib/errorReporting'
import type { TokenResponse } from '@/types/auth'
import { isReadOnlyRole } from '@/config/roles'
import { isBlockedForReadOnly, VIEW_ONLY_MESSAGE } from '@/lib/readOnly'

// Normalized API error surfaced to callers.
export interface ApiError {
  status: number
  detail: string
  requestId?: string
}

export function isApiError(e: unknown): e is ApiError {
  return typeof e === 'object' && e !== null && 'status' in e && 'detail' in e
}

// Main client (interceptors) + a bare client used only for refresh (avoids interceptor recursion).
export const api = axios.create({ baseURL: API_URL, timeout: 30_000 })
const rawClient = axios.create({ baseURL: API_URL, timeout: 30_000 })

// ── Request: attach Bearer; let axios set the multipart boundary for FormData ──
api.interceptors.request.use((config) => {
  // The principal is read-only (Omar, 31 Oct 2026). The server refuses his writes with 403; this
  // stops them before they leave, with the same words, so a control we missed cannot half-act.
  if (isReadOnlyRole(useAuthStore.getState().user?.role) && isBlockedForReadOnly(config.method, config.url)) {
    const err: ApiError = { status: 403, detail: VIEW_ONLY_MESSAGE }
    return Promise.reject(err)
  }
  const token = useAuthStore.getState().accessToken
  if (token) {
    config.headers.set('Authorization', `Bearer ${token}`)
  }
  if (config.data instanceof FormData) {
    config.headers.delete('Content-Type')
  }
  return config
})

// ── Single-flight refresh ──
// A2 note: the access token lives only in memory. On 401 we exchange the persisted refresh token
// (localStorage) for a new access token exactly once, then retry the original request. Concurrent
// 401s share one in-flight refresh. The refresh token in localStorage is a known, accepted XSS risk
// inherited from the Flutter app; the intended fix (httpOnly cookie) needs a backend change and is
// therefore out of scope — see SECURITY_NOTES.md.
let refreshPromise: Promise<string> | null = null

export async function refreshAccessToken(): Promise<string> {
  const refreshToken = useAuthStore.getState().getRefreshToken()
  if (!refreshToken) throw new Error('no-refresh-token')
  // NOTE: the backend /auth/refresh returns ONLY { access_token, token_type } — it does NOT rotate the
  // refresh token. So we update just the in-memory access token and KEEP the stored refresh token.
  // (Previously we wrote data.refresh_token, which was undefined → corrupted the stored token → the
  // next reload sent "undefined" and forced a logout. That was the "refresh → login screen" bug.)
  const { data } = await rawClient.post<TokenResponse>('/auth/refresh', { refresh_token: refreshToken })
  useAuthStore.getState().setAccessToken(data.access_token)
  return data.access_token
}

type RetriableConfig = InternalAxiosRequestConfig & { _retried?: boolean }

api.interceptors.response.use(
  (r) => r,
  async (error: AxiosError<{ detail?: string }>) => {
    // Already normalised (the principal's view-only stop in the request interceptor): pass it on.
    if (isApiError(error)) return Promise.reject(error)
    const cfg = error.config as RetriableConfig | undefined
    const status = error.response?.status
    const requestId = (error.response?.headers?.['x-request-id'] as string | undefined) ?? undefined
    const detail = error.response?.data?.detail ?? error.message

    // 401 → refresh + retry once (never for the refresh endpoint itself).
    const isRefreshCall = cfg?.url?.includes('/auth/refresh')
    if (status === 401 && cfg && !cfg._retried && !isRefreshCall) {
      cfg._retried = true
      try {
        if (!refreshPromise) {
          refreshPromise = refreshAccessToken().finally(() => {
            refreshPromise = null
          })
        }
        const newToken = await refreshPromise
        cfg.headers.set('Authorization', `Bearer ${newToken}`)
        return api(cfg)
      } catch {
        const msg =
          typeof detail === 'string' && detail.toLowerCase().includes('another device')
            ? detail
            : 'Your session has ended. Please sign in again.'
        useAuthStore.getState().forceLogout(msg)
        // fall through to normalized rejection below
      }
    }

    const apiError: ApiError = { status: status ?? 0, detail: String(detail), requestId }
    captureApiError(apiError, { url: cfg?.url, method: cfg?.method })
    return Promise.reject(apiError)
  },
)
